import React, { useState } from "react";
import { motion } from "framer-motion";
import { Shield, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

const LICENSE_TEXT = `By accessing and using this platform, you agree to the following terms and conditions:

1. You are authorized by your organization to administer this platform.
2. You will use this platform in accordance with your organization's policies and applicable laws.
3. You are responsible for managing user access and recognition activities within your organization.
4. All data entered into the platform remains the property of your organization.
5. You agree to maintain the confidentiality of any sensitive information accessed through this platform.
6. AddFlint reserves the right to update these terms with prior notice.

https://www.addflint.io/aup
For questions or concerns, please contact AddFlint support.`;

const LicenseAgreementModal = ({ onAccepted, onDeclined }) => {
  const [declining, setDeclining] = useState(false);
  const [accepting, setAccepting] = useState(false);

  if (declining) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-3">License Agreement Declined</h3>
          <p className="text-gray-600 mb-6">
            Please contact AddFlint for more clarification. You will need to sign in again and accept the agreement to access the platform.
          </p>
          <button
            onClick={onDeclined}
            className="w-full py-3 px-4 rounded-full text-white font-medium org-gradient shadow-md hover:shadow-lg transition-all"
          >
            Sign Out
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="org-gradient px-6 py-5">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-white mr-3" />
            <h2 className="text-xl font-bold text-white">License Agreement</h2>
          </div>
          <p className="text-white/80 text-sm mt-1">Please review and accept to continue</p>
        </div>

        {/* License Text */}
        <div className="px-6 py-4">
          <div className="bg-gray-50 rounded-2xl p-4 max-h-64 overflow-y-auto border border-gray-200">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
              {LICENSE_TEXT}
            </pre>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={() => setDeclining(true)}
            disabled={accepting}
            className="flex-1 py-3 px-4 rounded-full font-medium flex items-center justify-center transition-all border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <XCircle className="w-5 h-5 mr-2" />
            Decline
          </button>
          <button
            onClick={async () => {
              setAccepting(true);
              await onAccepted();
            }}
            disabled={accepting}
            className={`flex-1 py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
              accepting
                ? "bg-gray-400"
                : "org-gradient shadow-md hover:shadow-lg"
            }`}
          >
            {accepting ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <CheckCircle className="w-5 h-5 mr-2" />
            )}
            {accepting ? "Accepting..." : "Accept"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default LicenseAgreementModal;
